/**
 * AbsencesModal — manage per-worker absences that reduce weekly worker capacity.
 * Vacation / sick / training entries live in `capacity_worker_absences` and are
 * pulled into `/weeks` output so week cards + capacity math reflect them.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Umbrella, Stethoscope, GraduationCap, HelpCircle } from "lucide-react";
import { API, authHeader, inputCls, Field, ModalShell, WeekBadge } from "./shared";

const TYPE_META = {
  vacation: { label: "Ferie / Vacation", chip: "bg-emerald-500/20 text-emerald-200", Icon: Umbrella },
  sick:     { label: "Sick leave",       chip: "bg-red-500/20 text-red-200",         Icon: Stethoscope },
  training: { label: "Training",         chip: "bg-cyan-500/20 text-cyan-200",       Icon: GraduationCap },
  other:    { label: "Other",            chip: "bg-slate-500/20 text-slate-200",     Icon: HelpCircle },
};

function daysBetween(a, b) {
  if (!a || !b) return 0;
  try {
    const s = new Date(a);
    const f = new Date(b);
    if (isNaN(s) || isNaN(f)) return 0;
    return Math.floor((f - s) / 86400000) + 1;
  } catch { return 0; }
}

export default function AbsencesModal({ onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    worker_name: "",
    absence_type: "vacation",
    start_date: "",
    end_date: "",
    note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/absences`, authHeader());
      setRows(data.absences || []);
    } catch { toast.error("Failed to load absences"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.worker_name.trim() || !form.start_date || !form.end_date) {
      toast.error("Fill worker, start, finish");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be ≥ start date");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/absences`, form, authHeader());
      toast.success(`${form.worker_name} · ${TYPE_META[form.absence_type].label}`);
      setForm({ worker_name: "", absence_type: "vacation", start_date: "", end_date: "", note: "" });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this absence?")) return;
    try {
      await axios.delete(`${API}/absences/${id}`, authHeader());
      toast.success("Deleted");
      await load();
      onSaved?.();
    } catch { toast.error("Delete failed"); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-3xl" testid="absences-modal">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-black">Team & Capacity Planner</div>
          <h2 className="text-lg font-black text-slate-100">Worker absences</h2>
          <p className="text-[11px] text-slate-400 mt-1">
            Vacation, sick and training days reduce each affected week&apos;s worker-day capacity.
            Only business days (Mon-Fri) are counted.
          </p>
        </div>

        {/* Create form */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Add new absence</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Worker name *">
              <input value={form.worker_name} placeholder="e.g. Bogdan Popescu"
                     onChange={(e) => setForm({ ...form, worker_name: e.target.value })}
                     className={inputCls} data-testid="abs-name" autoFocus/>
            </Field>
            <Field label="Type *">
              <select value={form.absence_type}
                      onChange={(e) => setForm({ ...form, absence_type: e.target.value })}
                      className={inputCls} data-testid="abs-type">
                {Object.keys(TYPE_META).map((k) => (
                  <option key={k} value={k} className="bg-slate-900">{TYPE_META[k].label}</option>
                ))}
              </select>
            </Field>
            <Field label="Start date *">
              <div className="flex items-center gap-1">
                <input type="date" value={form.start_date} min="2020-01-01"
                       onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                       className={inputCls} data-testid="abs-start"/>
                <WeekBadge value={form.start_date} testId="abs-start-week"/>
              </div>
            </Field>
            <Field label="End date *">
              <div className="flex items-center gap-1">
                <input type="date" value={form.end_date}
                       onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                       className={inputCls} data-testid="abs-finish"/>
                <WeekBadge value={form.end_date} testId="abs-finish-week"/>
              </div>
            </Field>
            <Field label="Note (optional)">
              <input value={form.note} placeholder="e.g. Concediu Q1 planificat"
                     onChange={(e) => setForm({ ...form, note: e.target.value })}
                     className={inputCls} data-testid="abs-note"/>
            </Field>
            <div className="flex items-end">
              <button onClick={create} disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-[11px] uppercase tracking-widest font-black disabled:opacity-40"
                      data-testid="abs-add">
                {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Plus className="w-3 h-3"/>}
                Add absence
              </button>
            </div>
          </div>
          {form.start_date && form.end_date && (
            <div className="text-[10px] text-slate-500">
              Range: {daysBetween(form.start_date, form.end_date)} calendar days
            </div>
          )}
        </div>

        {/* Existing list */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-2">
            All absences ({rows.length})
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm italic">No absences yet.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="abs-list">
              {rows.map((a) => {
                const meta = TYPE_META[a.absence_type] || TYPE_META.other;
                const Icon = meta.Icon;
                const isActive = a.end_date >= today;
                return (
                  <li key={a.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${isActive ? "border-white/10 bg-white/5" : "border-white/5 bg-slate-800/40 opacity-70"}`}
                      data-testid={`abs-row-${a.id}`}>
                    <Icon className="w-4 h-4 text-cyan-300 flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-black text-slate-100 truncate">{a.worker_name}</div>
                        <span className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${meta.chip}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {a.start_date} → {a.end_date}
                        {a.note && <span className="ml-2 text-slate-500 italic">· {a.note}</span>}
                      </div>
                    </div>
                    <button onClick={() => remove(a.id)}
                            className="p-1.5 rounded text-slate-400 hover:text-red-300"
                            data-testid={`abs-delete-${a.id}`}
                            title="Delete">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-white/5 flex items-center justify-between">
        <div className="text-[10px] text-slate-500">Business days only · updates weekly capacity in real-time</div>
        <button onClick={onClose} className="text-[11px] text-slate-300 hover:text-slate-100"
                data-testid="abs-close">
          <X className="w-3 h-3 inline mr-1"/> Close
        </button>
      </div>
    </ModalShell>
  );
}
